import { useState, useEffect } from 'react';
import { TldrPromptModal } from './TldrPromptModal';
import {
  getTldrPrompt,
  updateTldrPrompt,
  type TldrPromptSettings,
} from '../repositories/tldrPromptRepository';
import { DEFAULT_DECAY, DEFAULT_RELEVANCE_MULTIPLIER } from '../scoringConfig';
import { IconEditSquare } from './icons';

interface TldrPromptEditorProps {
  /** Whether the current user/view may edit the TLDR feed prompt (admin on the TLDR feed). */
  canEdit: boolean;
}

/**
 * Self-contained editor for the TLDR feed prompt: the navbar pencil plus its
 * modal. Owns fetching and saving the settings so callers only tell it whether
 * editing is allowed — nothing else needs to thread the prompt state through.
 */
export function TldrPromptEditor({ canEdit }: TldrPromptEditorProps) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<TldrPromptSettings | null>(null);

  useEffect(() => {
    if (!canEdit || settings) return;
    getTldrPrompt().then(setSettings).catch(() => {});
  }, [canEdit, settings]);

  if (!canEdit || !settings) return null;

  const handleSubmit = async (values: {
    description: string;
    freshness_constant: number;
    relevance_constant: number;
  }) => {
    const updated = await updateTldrPrompt({
      description: values.description,
      decay_exponent: values.freshness_constant,
      relevance_boost_multiplier: values.relevance_constant,
    });
    setSettings(updated);
  };

  return (
    <>
      <button
        type="button"
        className="reader-navbar-prompt-edit"
        onClick={() => setOpen(true)}
        title="Edit TLDR feed prompt"
        aria-label="Edit TLDR feed prompt"
      >
        <IconEditSquare />
      </button>
      {open && (
        <TldrPromptModal
          description={settings.description ?? ''}
          decay={settings.decay_exponent ?? DEFAULT_DECAY}
          relevance={settings.relevance_boost_multiplier ?? DEFAULT_RELEVANCE_MULTIPLIER}
          onSubmit={handleSubmit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
